/**
 * Pantalla de salidas: emisión del documento (F1.5).
 *
 * ES EL PUNTO CRÍTICO DE TODA LA APLICACIÓN. Si nadie captura la matrícula no hay
 * documento, y sin documento el camión no debería salir. Todo el diseño de esta
 * pantalla persigue que ese paso sea de diez segundos y no se pueda omitir:
 *
 * - Tres campos y un botón. Nada más en la vista principal.
 * - La fecha viene rellena con hoy: es lo correcto en la práctica totalidad de
 *   los casos (art. 6 f).
 * - El transportista contratado se muestra grande, para que el operario pueda
 *   compararlo con lo que ve en el camión y confirmarlo o corregirlo (art. 7.2).
 * - El botón de emitir está deshabilitado hasta que los datos del art. 6 g) están
 *   completos, y el motivo se dice en pantalla.
 * - Tras emitir, lo primero que se ofrece es imprimir y entregar (apdo. Séptimo).
 */

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import Checkbox from '@mui/material/Checkbox'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Paper from '@mui/material/Paper'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EditIcon from '@mui/icons-material/Edit'
import LogoutIcon from '@mui/icons-material/Logout'
import GridViewIcon from '@mui/icons-material/GridView'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import PrintIcon from '@mui/icons-material/Print'
import SearchIcon from '@mui/icons-material/Search'
import SmsIcon from '@mui/icons-material/Sms'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { abrirBlob, api } from '../api/cliente'
import type {
  CanalEnvio,
  DatosEmision,
  DecaResumen,
  EmpresaSeleccion,
  RespuestaEmision,
} from '../api/tipos'
import { AlternarTema } from '../componentes/AlternarTema'
import { CampoAutocompletar } from '../componentes/CampoAutocompletar'
import { SelectorIdioma } from '../componentes/SelectorIdioma'
import { AvisoError, Cargando, referenciaConCamion } from '../componentes/comunes'
import { useCanales } from '../contexto/Canales'
import { useSesion } from '../contexto/Sesion'

type Paso = 'BUSCAR' | 'EMITIR' | 'EMITIDO' | 'MATRICULA'

function hoyISO(): string {
  const ahora = new Date()
  const desplazado = new Date(ahora.getTime() - ahora.getTimezoneOffset() * 60_000)
  return desplazado.toISOString().slice(0, 10)
}

/**
 * Ruta relativa del QR a partir de la URL pública del documento.
 *
 * `qr_png_url` apunta al subdominio público, que desde la red corporativa puede
 * no resolver (el firewall no suele permitir salir y volver a entrar por la IP
 * pública). El mismo endpoint responde por la ruta interna, así que se usa la
 * relativa: la imagen que ve el operario carga siempre.
 *
 * El enlace que se copia para el móvil del conductor sí es el absoluto: ese lo
 * abre un teléfono desde fuera.
 */
function qrRelativo(urlDocumento: string): string {
  const token = urlDocumento.split('/d/').pop() ?? ''
  return `/qr/${token}.png`
}

export function PantallaSalidas() {
  const { t } = useTranslation()
  const { credenciales, salir } = useSesion()
  const [searchParams] = useSearchParams()
  const navegar = useNavigate()

  const [paso, setPaso] = useState<Paso>('BUSCAR')
  const [texto, setTexto] = useState('')
  const [pendientes, setPendientes] = useState<DecaResumen[]>([])
  // Documentos ya emitidos que coinciden con la búsqueda: se ofrecen para cambiar
  // la matrícula cuando el transportista cambia de vehículo tras emitir (opción C).
  const [emitidos, setEmitidos] = useState<DecaResumen[]>([])
  const [buscando, setBuscando] = useState(false)
  const [seleccionado, setSeleccionado] = useState<DecaResumen | null>(null)
  const [empresa, setEmpresa] = useState<EmpresaSeleccion | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [emitiendo, setEmitiendo] = useState(false)
  const [emision, setEmision] = useState<RespuestaEmision | null>(null)
  const [aviso, setAviso] = useState('')

  // Envío del enlace al móvil del conductor por SMS/WhatsApp (apdo. 11.3). El
  // teléfono se pre-rellena con el del maestro y es editable: el conductor está
  // delante y su número puede no estar dado de alta o ser otro.
  const [telefono, setTelefono] = useState('')
  const [enviandoCanal, setEnviandoCanal] = useState<CanalEnvio | null>(null)
  // Solo se ofrecen los canales que un administrador ha activado.
  const canales = useCanales()

  // Cambio de matrícula (paso MATRICULA). Estado propio para no mezclarlo con el
  // formulario de emisión. `modoCambio` distingue la pantalla de éxito.
  // Cambio de datos del transporte sobre un documento ya emitido: matrículas y/o
  // transportista, en una sola pantalla pre-rellenada con lo vigente.
  const [nuevaTractora, setNuevaTractora] = useState('')
  const [nuevaRemolque, setNuevaRemolque] = useState('')
  const [nuevoNif, setNuevoNif] = useState('')
  const [nuevoNombre, setNuevoNombre] = useState('')
  const [motivoCambio, setMotivoCambio] = useState('')
  const [cambiando, setCambiando] = useState(false)
  const [modoCambio, setModoCambio] = useState(false)
  // Valores de partida, para detectar qué cambió de verdad.
  const [cambioInicial, setCambioInicial] = useState({
    tractora: '',
    remolque: '',
    nif: '',
    nombre: '',
  })

  // Datos del art. 6 e), f) y g).
  const [matriculaTractora, setMatriculaTractora] = useState('')
  const [matriculaRemolque, setMatriculaRemolque] = useState('')
  // Por defecto se asume vehículo rígido (sin remolque): es el caso más común en
  // salidas y evita exigir una matrícula de remolque que casi nunca aplica. El
  // operario lo desmarca cuando el camión lleva remolque.
  const [rigido, setRigido] = useState(true)
  const [fecha, setFecha] = useState(hoyISO())
  // Carta de porte (art. 10 bis Ley 15/2009): entrega prevista en destino y nº de
  // pedido del cliente. Se pre-rellenan con lo que trae el borrador.
  const [fechaEntrega, setFechaEntrega] = useState('')
  const [referenciaCliente, setReferenciaCliente] = useState('')
  const [confirmado, setConfirmado] = useState(false)
  const [otroTransportista, setOtroTransportista] = useState(false)
  const [nifReal, setNifReal] = useState('')
  const [nombreReal, setNombreReal] = useState('')
  const [autorizacion, setAutorizacion] = useState('')

  const campoTractora = useRef<HTMLInputElement>(null)

  const buscar = useCallback(
    async (consulta = texto) => {
      setBuscando(true)
      setError(null)
      try {
        // Se buscan a la vez los borradores (para emitir) y los ya emitidos (para
        // cambiar la matrícula): el operario escribe la referencia una sola vez y
        // ve el documento esté en el estado que esté.
        const [borradores, yaEmitidos] = await Promise.all([
          api.pendientes(consulta),
          api.emitidos(consulta),
        ])
        setPendientes(borradores)
        setEmitidos(yaEmitidos)
      } catch (fallo) {
        setError(fallo)
      } finally {
        setBuscando(false)
      }
    },
    [texto],
  )

  useEffect(() => {
    // Deep-link desde el detalle de un borrador (`/salidas?ref=…`): se busca el
    // envío y, si hay una única coincidencia pendiente, se abre directamente su
    // emisión, de modo que el borrador no quede «bloqueado» en el back-office.
    const ref = searchParams.get('ref')
    const camion = Number(searchParams.get('camion') ?? '1') || 1
    if (ref) {
      setTexto(ref)
      setBuscando(true)
      api
        .pendientes(ref)
        .then((lista) => {
          setPendientes(lista)
          const exacto =
            lista.find((d) => d.referencia_origen === ref && (d.numero_camion ?? 1) === camion) ??
            (lista.length === 1 ? lista[0] : null)
          if (exacto) elegir(exacto)
        })
        .catch((fallo) => setError(fallo))
        .finally(() => setBuscando(false))
    } else {
      void buscar()
    }
    // Si esta compañía exige la autorización del art. 6 e) (punto abierto A-07).
    // Se usa el endpoint de selección: salidas no tiene la clave de back-office
    // y con la ficha completa esta llamada fallaba en silencio, dejando el campo
    // sin mostrar en las empresas que sí lo exigen.
    api
      .empresasParaSeleccion()
      .then((lista) =>
        setEmpresa(lista.find((e) => e.empresa_id === credenciales?.empresaId) ?? null),
      )
      .catch(() => setEmpresa(null))
    // Solo al montar: la búsqueda posterior la lanza el operario.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function elegir(documento: DecaResumen) {
    setSeleccionado(documento)
    // Si la carga trajo las matrículas, se pre-rellenan; el operario las
    // confirma o corrige antes de emitir contra el vehículo real.
    setMatriculaTractora(documento.matricula_tractora_prevista ?? '')
    setMatriculaRemolque(documento.matricula_remolque_prevista ?? '')
    // Rígido por defecto (ver el estado inicial). Si la carga trajo matrícula de
    // remolque, se asume que hay remolque y se desmarca.
    setRigido(!(documento.matricula_remolque_prevista ?? '').trim())
    setFecha(hoyISO())
    setFechaEntrega(documento.fecha_entrega_estimada ?? '')
    setReferenciaCliente(documento.referencia_cliente ?? '')
    setConfirmado(false)
    setOtroTransportista(false)
    setNifReal('')
    setNombreReal('')
    setAutorizacion('')
    setError(null)
    setPaso('EMITIR')
    window.setTimeout(() => campoTractora.current?.focus(), 100)
  }

  function elegirEmitido(documento: DecaResumen) {
    setSeleccionado(documento)
    // Se pre-rellena con lo vigente: el operario corrige solo lo que cambió (la
    // matrícula del nuevo vehículo, el transportista, o ambos). El PDF mostrará
    // tachado lo que de verdad cambie.
    const tractora = documento.matricula_tractora_actual ?? ''
    const remolque = documento.matricula_remolque_actual ?? ''
    const nif = documento.transportista_nif_real ?? ''
    const nombre = documento.transportista_nombre_real ?? ''
    setNuevaTractora(tractora)
    setNuevaRemolque(remolque)
    setNuevoNif(nif)
    setNuevoNombre(nombre)
    setCambioInicial({ tractora, remolque, nif, nombre })
    setMotivoCambio('')
    setModoCambio(false)
    setError(null)
    setPaso('MATRICULA')
    window.setTimeout(() => campoTractora.current?.focus(), 100)
  }

  /** Qué falta para poder guardar: motivo y que algo haya cambiado de verdad. */
  const faltanCambio = useMemo(() => {
    if (motivoCambio.trim().length < 3) return true
    const hayCambio =
      nuevaTractora.trim() !== cambioInicial.tractora ||
      nuevaRemolque.trim() !== cambioInicial.remolque ||
      nuevoNif.trim() !== cambioInicial.nif ||
      nuevoNombre.trim() !== cambioInicial.nombre
    return !hayCambio
  }, [nuevaTractora, nuevaRemolque, nuevoNif, nuevoNombre, motivoCambio, cambioInicial])

  async function guardarCambio() {
    if (!seleccionado || faltanCambio) return
    setCambiando(true)
    setError(null)
    // Solo se envían los campos que de verdad cambiaron: el backend marca como
    // sustituido lo que difiere, y así no se toca lo que no cambia.
    const transportistaCambio =
      nuevoNif.trim() !== cambioInicial.nif || nuevoNombre.trim() !== cambioInicial.nombre
    try {
      const respuesta = await api.cambiarTransporte(seleccionado.uuid, {
        motivo: motivoCambio.trim(),
        matricula_tractora:
          nuevaTractora.trim() !== cambioInicial.tractora ? nuevaTractora.trim() : undefined,
        matricula_remolque:
          nuevaRemolque.trim() !== cambioInicial.remolque ? nuevaRemolque.trim() : undefined,
        transportista_nif: transportistaCambio ? nuevoNif.trim() : undefined,
        transportista_nombre: transportistaCambio ? nuevoNombre.trim() : undefined,
      })
      // Se reutiliza la pantalla de éxito de la emisión: mismo QR/enlace, y desde
      // ahí se puede reimprimir y volver a entregar.
      setEmision(respuesta)
      setModoCambio(true)
      setPaso('EMITIDO')
    } catch (fallo) {
      setError(fallo)
    } finally {
      setCambiando(false)
    }
  }

  /** Qué falta para poder emitir. Se muestra en pantalla, no se deja adivinar. */
  const faltan = useMemo(() => {
    const lista: string[] = []
    if (!matriculaTractora.trim()) lista.push(t('salidas.matriculaTractora'))
    if (!rigido && !matriculaRemolque.trim()) lista.push(t('salidas.matriculaRemolque'))
    if (!fecha) lista.push(t('salidas.fechaTransporte'))
    if (fechaEntrega && fecha && fechaEntrega < fecha) lista.push(t('salidas.entregaAnterior'))
    if (!confirmado) lista.push(t('salidas.transportista'))
    if (otroTransportista && (!nifReal.trim() || !nombreReal.trim())) {
      lista.push(t('salidas.nifReal'))
    }
    if (empresa?.exige_autorizacion_especial && !autorizacion.trim()) {
      lista.push(t('salidas.autorizacion'))
    }
    return lista
  }, [
    matriculaTractora,
    matriculaRemolque,
    rigido,
    fecha,
    fechaEntrega,
    confirmado,
    otroTransportista,
    nifReal,
    nombreReal,
    autorizacion,
    empresa,
    t,
  ])

  async function emitir() {
    if (!seleccionado || faltan.length > 0) return
    setEmitiendo(true)
    setError(null)
    const datos: DatosEmision = {
      matricula_tractora: matriculaTractora.trim(),
      matricula_remolque: rigido ? '' : matriculaRemolque.trim(),
      vehiculo_rigido: rigido,
      fecha_transporte: fecha,
      fecha_entrega_estimada: fechaEntrega || null,
      referencia_cliente: referenciaCliente.trim(),
      transportista_confirmado: confirmado,
      transportista_nif_real: otroTransportista ? nifReal.trim() : '',
      transportista_nombre_real: otroTransportista ? nombreReal.trim() : '',
      autorizacion_especial: autorizacion.trim(),
      observaciones: '',
    }
    try {
      setEmision(await api.emitir(seleccionado.uuid, datos))
      setPaso('EMITIDO')
    } catch (fallo) {
      setError(fallo)
    } finally {
      setEmitiendo(false)
    }
  }

  async function imprimir() {
    if (!seleccionado) return
    setError(null)
    try {
      const blob = await api.pdfDeca(seleccionado.uuid)
      abrirBlob(blob)
      // La entrega se registra al imprimir: es la evidencia del apdo. Séptimo.
      await api.registrarEntrega(seleccionado.uuid, 'IMPRESO')
      setAviso(t('salidas.entregaRegistrada'))
    } catch (fallo) {
      setError(fallo)
    }
  }

  async function copiarEnlace() {
    if (!emision) return
    try {
      await navigator.clipboard.writeText(emision.url)
      setAviso(t('salidas.enlaceCopiado'))
      await api.registrarEntrega(emision.uuid, 'ELECTRONICO')
    } catch {
      setAviso(emision.url)
    }
  }

  // Al emitir (o tras cambiar la matrícula) se pre-rellena el teléfono con el del
  // maestro, si lo hay.
  useEffect(() => {
    setTelefono(emision?.telefono_transportista ?? '')
  }, [emision])

  async function enviarPorCanal(canal: CanalEnvio) {
    if (!emision) return
    setEnviandoCanal(canal)
    setError(null)
    try {
      // El backend registra la entrega ELECTRONICO cuando el envío se produce.
      const respuesta = await api.enviarEnlace(emision.uuid, canal, telefono.trim() || undefined)
      // La pasarela nunca lanza: si no se envió, explica por qué (apagada, sin
      // teléfono, sin plantilla de WhatsApp…) y el operario conserva enlace y QR.
      setAviso(respuesta.enviado ? t('salidas.enlaceEnviado') : respuesta.error ?? t('salidas.envioFallido'))
    } catch (fallo) {
      setError(fallo)
    } finally {
      setEnviandoCanal(null)
    }
  }

  function siguiente() {
    setPaso('BUSCAR')
    setSeleccionado(null)
    setEmision(null)
    setModoCambio(false)
    setTexto('')
    setError(null)
    void buscar()
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 3 }}>
        {paso !== 'BUSCAR' && paso !== 'MATRICULA' && (
          <IconButton onClick={paso === 'EMITIDO' ? siguiente : () => setPaso('BUSCAR')} size="large">
            <ArrowBackIcon />
          </IconButton>
        )}
        <LocalShippingIcon color="primary" fontSize="large" />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
            {t('salidas.titulo')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {credenciales?.empresaId} · {credenciales?.usuario}
          </Typography>
        </Box>
        <SelectorIdioma />
        <AlternarTema color="primary" />
        {paso === 'BUSCAR' && (
          // El manual, para el operario que solo tiene 'salidas' y no ve la
          // barra del back-office. Como el menú, solo en la portada.
          <IconButton
            color="primary"
            onClick={() => navegar('/admin/manual')}
            title={t('admin.manual')}
            aria-label={t('admin.manual')}
          >
            <MenuBookIcon />
          </IconButton>
        )}
        {paso === 'BUSCAR' && (
          // Vuelta al back-office. Solo en la portada de salidas: durante la
          // emisión la esquina izquierda es el «atrás» del flujo, y no se ofrece
          // salir para no abandonar una emisión a medias por accidente.
          <IconButton
            color="primary"
            onClick={() => navegar('/admin')}
            title={t('salidas.menuPrincipal')}
            aria-label={t('salidas.menuPrincipal')}
          >
            <GridViewIcon />
          </IconButton>
        )}
        {paso === 'BUSCAR' && (
          // Salir de verdad (con Keycloak, también de la sesión corporativa): en
          // una tableta compartida, la siguiente persona entra con su usuario y
          // la auditoría registra a quien de verdad emite.
          <IconButton
            color="primary"
            onClick={salir}
            title={t('app.salir')}
            aria-label={t('app.salir')}
          >
            <LogoutIcon />
          </IconButton>
        )}
        {paso === 'MATRICULA' && (
          // El cambio de matrícula no es una emisión a medias, pero el menú no
          // pinta nada aquí: la esquina derecha es el «atrás» que vuelve a la
          // lista de búsqueda.
          <IconButton onClick={() => setPaso('BUSCAR')} size="large" aria-label={t('salidas.buscarEnvio')}>
            <ArrowBackIcon />
          </IconButton>
        )}
      </Stack>

      <AvisoError error={error} />

      {paso === 'BUSCAR' && (
        <Box>
          <TextField
            fullWidth
            autoFocus
            label={t('salidas.buscarEnvio')}
            helperText={t('salidas.buscarAyuda')}
            value={texto}
            onChange={(evento) => setTexto(evento.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === 'Enter') void buscar()
            }}
           
            sx={{ mb: 3 }}
           slotProps={{ input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => void buscar()} size="large">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            } }}/>

          {buscando ? (
            <Cargando />
          ) : pendientes.length === 0 && emitidos.length === 0 ? (
            <Alert severity="info">{t('salidas.sinPendientes')}</Alert>
          ) : (
            <Stack spacing={1.5}>
              {pendientes.map((documento) => (
                <Card key={documento.uuid}>
                  <CardActionArea onClick={() => elegir(documento)}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {referenciaConCamion(documento)}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {documento.transportista_nombre_real} ·{' '}
                        {documento.transportista_nif_real}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}

              {emitidos.length > 0 && (
                <>
                  <Divider sx={{ mt: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      {t('salidas.emitidosTitulo')}
                    </Typography>
                  </Divider>
                  {emitidos.map((documento) => (
                    <Card key={documento.uuid} variant="outlined">
                      <CardActionArea onClick={() => elegirEmitido(documento)}>
                        <CardContent>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                          >
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              {referenciaConCamion(documento)}
                            </Typography>
                            <EditIcon fontSize="small" color="action" />
                          </Stack>
                          <Typography variant="body1" color="text.secondary">
                            {documento.transportista_nombre_real} ·{' '}
                            {documento.transportista_nif_real}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  ))}
                </>
              )}
            </Stack>
          )}
        </Box>
      )}

      {paso === 'MATRICULA' && seleccionado && (
        <Box>
          <Paper sx={{ p: 2.5, mb: 3, bgcolor: 'action.hover' }}>
            <Typography variant="overline" color="text.secondary">
              {t('salidas.cambiarTransporte')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {referenciaConCamion(seleccionado)}
            </Typography>
          </Paper>

          <Alert severity="info" icon={false} sx={{ mb: 2 }}>
            {t('salidas.cambiarTransporteAyuda')}
          </Alert>

          <Typography variant="overline" color="text.secondary">
            {t('salidas.matriculas')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, mt: 1 }}>
            <TextField
              inputRef={campoTractora}
              label={t('salidas.matriculaTractora')}
              value={nuevaTractora}
              onChange={(evento) => setNuevaTractora(evento.target.value.toUpperCase())}
              fullWidth
              autoComplete="off"
              slotProps={{ htmlInput: { maxLength: 15 } }}
            />
            <TextField
              label={t('salidas.matriculaRemolque')}
              value={nuevaRemolque}
              onChange={(evento) => setNuevaRemolque(evento.target.value.toUpperCase())}
              fullWidth
              autoComplete="off"
              slotProps={{ htmlInput: { maxLength: 15 } }}
            />
          </Stack>

          <Typography variant="overline" color="text.secondary">
            {t('salidas.transportista')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, mt: 1 }}>
            <CampoAutocompletar
              label={t('salidas.nifReal')}
              campo="transportista_nif"
              value={nuevoNif}
              onChange={setNuevoNif}
              onSeleccion={(s) => {
                setNuevoNif(s.valor)
                if (s.secundario) setNuevoNombre(s.secundario)
              }}
              mayusculas
              fullWidth
              maxLength={15}
              sx={{ maxWidth: { sm: 220 } }}
            />
            <CampoAutocompletar
              label={t('salidas.nombreReal')}
              campo="transportista_nombre"
              value={nuevoNombre}
              onChange={setNuevoNombre}
              onSeleccion={(s) => {
                setNuevoNombre(s.valor)
                if (s.secundario) setNuevoNif(s.secundario)
              }}
              fullWidth
              maxLength={255}
            />
          </Stack>

          <TextField
            label={t('salidas.motivoCambio')}
            helperText={t('salidas.motivoCambioAyuda')}
            value={motivoCambio}
            onChange={(evento) => setMotivoCambio(evento.target.value)}
            fullWidth
            required
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />

          {faltanCambio && (
            <Alert severity="warning" sx={{ mt: 3 }}>
              {t('salidas.faltanDatosTransporte')}
            </Alert>
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={() => void guardarCambio()}
            disabled={faltanCambio || cambiando}
            sx={{ mt: 3, py: 2.5, fontSize: '1.3rem' }}
          >
            {cambiando ? t('salidas.guardando') : t('salidas.guardarCambio')}
          </Button>
        </Box>
      )}

      {paso === 'EMITIR' && seleccionado && (
        <Box>
          <Paper sx={{ p: 2.5, mb: 3, bgcolor: 'action.hover' }}>
            <Typography variant="overline" color="text.secondary">
              {t('salidas.envio')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {referenciaConCamion(seleccionado)}
            </Typography>
          </Paper>

          <Typography variant="overline" color="text.secondary">
            {t('salidas.matriculas')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1, mt: 1 }}>
            <TextField
              inputRef={campoTractora}
              label={t('salidas.matriculaTractora')}
              value={matriculaTractora}
              onChange={(evento) => setMatriculaTractora(evento.target.value.toUpperCase())}
              fullWidth
              required
              autoComplete="off"
             
             slotProps={{ htmlInput: { maxLength: 15 } }}/>
            <TextField
              label={t('salidas.matriculaRemolque')}
              value={matriculaRemolque}
              onChange={(evento) => setMatriculaRemolque(evento.target.value.toUpperCase())}
              fullWidth
              required={!rigido}
              disabled={rigido}
              autoComplete="off"
             
             slotProps={{ htmlInput: { maxLength: 15 } }}/>
          </Stack>
          <FormControlLabel
            control={
              <Checkbox checked={rigido} onChange={(evento) => setRigido(evento.target.checked)} />
            }
            label={t('salidas.vehiculoRigido')}
            sx={{ mb: 2 }}
          />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              type="date"
              label={t('salidas.fechaTransporte')}
              value={fecha}
              onChange={(evento) => setFecha(evento.target.value)}
              fullWidth
              required
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              type="date"
              label={t('salidas.fechaEntrega')}
              value={fechaEntrega}
              onChange={(evento) => setFechaEntrega(evento.target.value)}
              fullWidth
              error={Boolean(fechaEntrega && fecha && fechaEntrega < fecha)}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: fecha } }}
            />
          </Stack>
          <TextField
            label={t('salidas.referenciaCliente')}
            value={referenciaCliente}
            onChange={(evento) => setReferenciaCliente(evento.target.value)}
            fullWidth
            autoComplete="off"
            sx={{ mb: 3 }}
            slotProps={{ htmlInput: { maxLength: 60 } }}
          />

          <Divider sx={{ mb: 2 }} />

          <Typography variant="overline" color="text.secondary">
            {t('salidas.transportista')}
          </Typography>
          <Paper sx={{ p: 2, mt: 1, mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('salidas.transportistaPrevisto')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {seleccionado.transportista_nombre_real}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {seleccionado.transportista_nif_real}
            </Typography>
          </Paper>

          <FormControlLabel
            control={
              <Checkbox
                checked={confirmado}
                onChange={(evento) => setConfirmado(evento.target.checked)}
              />
            }
            label={t('salidas.confirmarTransportista')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={otroTransportista}
                onChange={(evento) => {
                  setOtroTransportista(evento.target.checked)
                  if (!evento.target.checked) {
                    setNifReal('')
                    setNombreReal('')
                  }
                }}
              />
            }
            label={t('salidas.otroTransportista')}
          />

          {otroTransportista && (
            <Stack spacing={2} sx={{ mt: 1.5, mb: 1 }}>
              <CampoAutocompletar
                label={t('salidas.nifReal')}
                campo="transportista_nif"
                value={nifReal}
                onChange={setNifReal}
                onSeleccion={(s) => {
                  setNifReal(s.valor)
                  if (s.secundario) setNombreReal(s.secundario)
                }}
                mayusculas
                required
                fullWidth
                maxLength={15}
              />
              <CampoAutocompletar
                label={t('salidas.nombreReal')}
                campo="transportista_nombre"
                value={nombreReal}
                onChange={setNombreReal}
                onSeleccion={(s) => {
                  setNombreReal(s.valor)
                  if (s.secundario) setNifReal(s.secundario)
                }}
                required
                fullWidth
                maxLength={255}
              />
            </Stack>
          )}

          {empresa?.exige_autorizacion_especial && (
            <TextField
              label={t('salidas.autorizacion')}
              value={autorizacion}
              onChange={(evento) => setAutorizacion(evento.target.value)}
              fullWidth
              required
              sx={{ mt: 2 }}
             
             slotProps={{ htmlInput: { maxLength: 120 } }}/>
          )}

          {faltan.length > 0 && (
            <Alert severity="warning" sx={{ mt: 3 }}>
              {t('salidas.faltanDatos')}: {faltan.join(', ')}
            </Alert>
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={() => void emitir()}
            disabled={faltan.length > 0 || emitiendo}
            sx={{ mt: 3, py: 2.5, fontSize: '1.3rem' }}
          >
            {emitiendo ? t('salidas.emitiendo') : t('salidas.emitir')}
          </Button>
        </Box>
      )}

      {paso === 'EMITIDO' && emision && (
        <Box>
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
              {modoCambio ? t('salidas.matriculaCambiada') : t('salidas.emitido')}
            </Typography>
            {seleccionado && referenciaConCamion(seleccionado)}
          </Alert>

          <Alert severity="warning" icon={false} sx={{ mb: 3 }}>
            <strong>{t('salidas.avisoEntrega')}</strong>
            <br />
            {t('salidas.avisoManuscrito')}
          </Alert>

          <Button
            variant="contained"
            fullWidth
            startIcon={<PrintIcon />}
            onClick={() => void imprimir()}
            sx={{ py: 2.5, fontSize: '1.25rem', mb: 1 }}
          >
            {t('salidas.imprimir')}
          </Button>
          {/* Carta de porte, art. 11 Ley 15/2009: tres ejemplares firmados. */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', fontWeight: 600, mb: 3 }}
          >
            {t('salidas.tresCopias')}
          </Typography>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('salidas.enlaceMovil')}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography
                variant="body2"
                sx={{ wordBreak: 'break-all', flexGrow: 1, fontFamily: 'monospace' }}
              >
                {emision.url}
              </Typography>
              <IconButton onClick={() => void copiarEnlace()} title={t('salidas.copiarEnlace')}>
                <ContentCopyIcon />
              </IconButton>
            </Stack>

            {(canales.whatsapp || canales.sms) && (
              <>
                <Divider sx={{ my: 2 }} />

                {/* Envío del enlace al móvil del conductor (apdo. 11.3). El teléfono
                    se pre-rellena con el del maestro y es editable. */}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {t('salidas.enviarEnlace')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
                  <TextField
                    label={t('salidas.telefonoConductor')}
                    type="tel"
                    value={telefono}
                    onChange={(evento) => setTelefono(evento.target.value)}
                    fullWidth
                    autoComplete="off"
                    slotProps={{ htmlInput: { maxLength: 30 } }}
                  />
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  {canales.whatsapp && (
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<WhatsAppIcon />}
                      disabled={enviandoCanal !== null || !telefono.trim()}
                      onClick={() => void enviarPorCanal('whatsapp')}
                      sx={{ py: 1.5 }}
                    >
                      {enviandoCanal === 'whatsapp' ? t('salidas.enviando') : 'WhatsApp'}
                    </Button>
                  )}
                  {canales.sms && (
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<SmsIcon />}
                      disabled={enviandoCanal !== null || !telefono.trim()}
                      onClick={() => void enviarPorCanal('sms')}
                      sx={{ py: 1.5 }}
                    >
                      {enviandoCanal === 'sms' ? t('salidas.enviando') : 'SMS'}
                    </Button>
                  )}
                </Stack>
              </>
            )}

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <img
                src={qrRelativo(emision.url)}
                alt="QR"
                width={180}
                height={180}
                style={{ imageRendering: 'pixelated' }}
              />
            </Box>
          </Paper>

          <Button variant="outlined" fullWidth onClick={siguiente} sx={{ py: 2 }}>
            {t('salidas.siguienteCamion')}
          </Button>
        </Box>
      )}

      <Snackbar
        open={aviso !== ''}
        message={aviso}
        autoHideDuration={4000}
        onClose={() => setAviso('')}
      />
    </Container>
  )
}
